import { Link } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { centralApi } from '@marque/api/central'
import type { TenantSummary } from '@marque/api/central'

const STALE = 2 * 60 * 1000

function KpiCard({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
    return (
        <div className="bg-white rounded-lg border border-neutral-200 p-5">
            <p className="text-xs font-medium text-neutral-500 uppercase tracking-wide">{label}</p>
            <p className="text-3xl font-semibold text-neutral-900 mt-1">{value}</p>
            {sub && <p className="text-xs text-neutral-400 mt-1">{sub}</p>}
        </div>
    )
}

function StatusBreakdown({ tenants }: { tenants: TenantSummary[] }) {
    const order = ['Active', 'PendingBootstrap', 'Suspended', 'Archived']
    const colours: Record<string, string> = {
        Active:           'bg-green-100 text-green-800',
        PendingBootstrap: 'bg-blue-100 text-blue-800',
        Suspended:        'bg-yellow-100 text-yellow-800',
        Archived:         'bg-neutral-100 text-neutral-500',
    }
    const counts: Record<string, number> = Object.fromEntries(order.map(s => [s, 0]))
    for (const t of tenants) { counts[t.status] = (counts[t.status] ?? 0) + 1 }

    return (
        <div className="bg-white rounded-lg border border-neutral-200 p-5">
            <p className="text-xs font-medium text-neutral-500 uppercase tracking-wide mb-3">By status</p>
            <dl className="space-y-2">
                {order.filter(s => (counts[s] ?? 0) > 0).map(s => (
                    <div key={s} className="flex flex-wrap items-center justify-between gap-3">
                        <span className={`text-xs px-2 py-0.5 rounded font-medium ${colours[s] ?? 'bg-neutral-100 text-neutral-600'}`}>
                            {s}
                        </span>
                        <span className="text-sm font-semibold text-neutral-900">{counts[s] ?? 0}</span>
                    </div>
                ))}
            </dl>
        </div>
    )
}

function RingBreakdown({ tenants }: { tenants: TenantSummary[] }) {
    const ringCounts = new Map<string, number>()
    for (const t of tenants) { ringCounts.set(t.ring, (ringCounts.get(t.ring) ?? 0) + 1) }
    const sorted = [...ringCounts.entries()].sort((a, b) => a[0].localeCompare(b[0]))

    return (
        <div className="bg-white rounded-lg border border-neutral-200 p-5">
            <p className="text-xs font-medium text-neutral-500 uppercase tracking-wide mb-3">By ring</p>
            <dl className="space-y-2">
                {sorted.map(([ring, count]) => (
                    <div key={ring} className="flex flex-wrap items-center justify-between gap-3">
                        <span className="text-sm font-mono text-neutral-700">{ring}</span>
                        <span className="text-sm font-semibold text-neutral-900">{count}</span>
                    </div>
                ))}
                {sorted.length === 0 && <p className="text-sm text-neutral-400">No data</p>}
            </dl>
        </div>
    )
}

function RecentTenants({ tenants }: { tenants: TenantSummary[] }) {
    const recent = [...tenants]
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .slice(0, 10)

    return (
        <div className="bg-white rounded-lg border border-neutral-200 overflow-x-auto">
            <div className="px-5 py-4 border-b border-neutral-100">
                <p className="text-xs font-medium text-neutral-500 uppercase tracking-wide">Recently provisioned</p>
            </div>
            <table className="min-w-full divide-y divide-neutral-100">
                <tbody className="divide-y divide-neutral-50">
                    {recent.map(t => (
                        <tr key={t.tenantId} className="hover:bg-neutral-50">
                            <td className="px-5 py-3 text-sm font-medium text-neutral-900">
                                <Link
                                    to="/tenants/$slug"
                                    params={{ slug: t.slug }}
                                    className="hover:text-blue-600"
                                >
                                    {t.displayName}
                                </Link>
                            </td>
                            <td className="px-5 py-3 text-sm font-mono text-neutral-500">{t.slug}</td>
                            <td className="px-5 py-3 text-sm text-neutral-400">
                                {new Date(t.createdAt).toLocaleDateString()}
                            </td>
                            <td className="px-5 py-3 text-sm font-mono text-neutral-400">{t.ring}</td>
                        </tr>
                    ))}
                    {recent.length === 0 && (
                        <tr>
                            <td colSpan={4} className="px-5 py-6 text-center text-sm text-neutral-400">
                                No tenants yet.
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    )
}

export function DashboardPage() {
    const { data, isLoading, isError } = useQuery({
        queryKey: ['tenants-all'],
        queryFn: centralApi.listAllTenants,
        staleTime: STALE,
    })

    const tenants = data?.items ?? []
    const active = tenants.filter(t => t.status === 'Active').length
    const pending = tenants.filter(t => t.status === 'PendingBootstrap').length

    return (
        <div className="space-y-6">
            <h1 className="text-2xl font-semibold text-neutral-900">Operations dashboard</h1>

            {isLoading && <p className="text-sm text-neutral-500">Loading…</p>}
            {isError && <p className="text-sm text-red-600">Failed to load tenant data.</p>}

            {data && (
                <>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <KpiCard label="Total tenants" value={data.totalCount} />
                        <KpiCard label="Active" value={active} />
                        <KpiCard label="Pending bootstrap" value={pending} sub="awaiting first sign-in" />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <StatusBreakdown tenants={tenants} />
                        <RingBreakdown tenants={tenants} />
                    </div>

                    <RecentTenants tenants={tenants} />
                </>
            )}
        </div>
    )
}
